"use client";

import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from "react-router-dom";
import { FaCalendarAlt, FaClock, FaMapMarkerAlt } from "react-icons/fa";
import { PiShoppingCartDuotone, PiMagnifyingGlassDuotone } from "react-icons/pi";

import Loading from "../../components/Loading";
import { SkeletonEventsPage } from "../../components/Skeleton";
import api from "../../helpers/Api";
import moment from "moment-timezone";

function EventCard({ event, onBuyClick }) {
    const URL_ORIGIN = process.env.NODE_ENV === "development"
        ? process.env.REACT_APP_API_URL_DEV
        : process.env.REACT_APP_API_URL;

    const primaryImage = event.event_images?.find(img => img.is_primary);
    const imageUrl = primaryImage ? `${URL_ORIGIN}/image-proxy?id=${primaryImage.id}` : null;
    const isPast = moment(event.event_date).isBefore(moment());

    return (
        <div className="group flex flex-col bg-[#16151c] border border-white/10 hover:border-brand/30 rounded-2xl overflow-hidden transition-all duration-300 shadow-xl">
            <div className="relative h-52 overflow-hidden bg-[#0f0e17]">
                {imageUrl && (
                    <img
                        src={imageUrl}
                        alt={event.event_name}
                        loading="lazy"
                        decoding="async"
                        className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${isPast ? "grayscale opacity-60" : ""}`}
                    />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#16151c] via-transparent to-transparent opacity-80" />
                <span className={`absolute top-3 right-3 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${isPast ? "bg-white/10 border-white/20 text-gray-300" : "bg-brand/20 border-brand/40 text-brand"}`}>
                    {isPast ? "Finalizado" : "Próximo"}
                </span>
            </div>

            <div className="flex flex-col flex-1 p-5 gap-3">
                <h5 className="font-display text-lg font-bold text-white leading-tight line-clamp-2">
                    {event.event_name}
                </h5>

                <div className="flex flex-col gap-1.5 text-xs text-gray-400">
                    <span className="flex items-center gap-1.5">
                        <FaCalendarAlt className="text-brand shrink-0" />
                        <span className="capitalize">
                            {moment(event.event_date).utcOffset(0, true).format("D [de] MMMM, YYYY")}
                        </span>
                    </span>
                    <span className="flex items-center gap-1.5">
                        <FaClock className="text-brand shrink-0" />
                        {moment(event.event_date).utcOffset(0, true).format("hh:mm a")}
                    </span>
                    <span className="flex items-center gap-1.5">
                        <FaMapMarkerAlt className="text-brand shrink-0" />
                        <span className="truncate">{event.event_location}</span>
                    </span>
                </div>

                {event.event_description && (
                    <p className="text-gray-400 text-xs leading-relaxed line-clamp-3">
                        {event.event_description}
                    </p>
                )}

                <div className="mt-auto pt-2">
                    {isPast ? (
                        <button disabled className="thm-btn event-one__btn w-full opacity-50 cursor-not-allowed">
                            <span>Evento finalizado</span>
                        </button>
                    ) : (
                        <button onClick={() => onBuyClick(event.event_id)} className="thm-btn event-one__btn w-full">
                            <PiShoppingCartDuotone size={18} />
                            <span>Comprar Boletos</span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export function Render({ events }) {
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const eventsPerPage = 9;
    const navigate = useNavigate();

    const routeChange = (id) => navigate(`/event/detail/${id}`);

    const filteredEvents = useMemo(() => events.filter(event =>
        event.event_name.toLowerCase().includes(searchTerm.toLowerCase())
    ), [events, searchTerm]);

    const totalPages = Math.ceil(filteredEvents.length / eventsPerPage);
    const currentEvents = filteredEvents.slice(
        (currentPage - 1) * eventsPerPage,
        currentPage * eventsPerPage
    );

    return (
        <div className="bg-[#0f0e17] min-h-screen py-14 px-4">
            <div className="max-w-7xl mx-auto">

                <div className="text-center mb-10">
                    <span className="font-mono text-brand font-semibold text-sm uppercase tracking-widest">Cartelera</span>
                    <h2 className="font-display text-white font-bold text-3xl sm:text-4xl mt-2">Todos los Eventos</h2>
                </div>

                <div className="mb-8 max-w-lg mx-auto">
                    <div className="relative">
                        <PiMagnifyingGlassDuotone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg pointer-events-none" />
                        <input
                            type="text"
                            placeholder="Buscar eventos..."
                            className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/15 rounded-full text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand/50 transition"
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                        />
                    </div>
                </div>

                {currentEvents.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {currentEvents.map((event) => (
                            <EventCard key={event.event_id} event={event} onBuyClick={routeChange} />
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-20">
                        <p className="text-gray-400 text-lg">No se encontraron eventos.</p>
                    </div>
                )}

                {totalPages > 1 && (
                    <div className="flex justify-center gap-2 mt-12">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                            <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                className={`w-10 h-10 rounded-full text-sm font-semibold border transition-all ${page === currentPage
                                        ? "bg-brand border-brand text-white"
                                        : "bg-white/5 border-white/10 text-gray-400 hover:border-brand/50 hover:text-white"
                                    }`}
                            >
                                {page}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default function AllEvents() {
    const [events, setEvents] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await api.get(`/Event/All`);
                const sorted = res.data.sort((a, b) => {
                    const now = moment();
                    const aIsPast = moment(a.event_date).isBefore(now);
                    const bIsPast = moment(b.event_date).isBefore(now);
                    if (aIsPast && !bIsPast) return 1;
                    if (!aIsPast && bIsPast) return -1;
                    return moment(a.event_date) - moment(b.event_date);
                });
                setEvents(sorted);
            } catch (error) {
                console.error("Error fetching events:", error);
            }
        };
        fetchData();
    }, []);

    if (!events) return <SkeletonEventsPage />;
    return <Render events={events} />;
}
